import { createAnytimeEmploymentContractApi } from "@/api/services/main/any-time/employment-contract";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { ButtonGroup } from "@/features/anytime/components/anytime-detail/ButtonGroup";
import { CompanySupporterForm } from "@/features/anytime/components/anytime-detail/CompanySupporterForm";
import { EligibleForeignNationals } from "@/features/anytime/components/anytime-detail/EligibleForeignNationals";
import { ReasonForm } from "@/features/anytime/components/anytime-detail/employment-contract/ReasonForm";
import { SpecificSkillsAffiliation } from "@/features/anytime/components/anytime-detail/SpecificSkillsAffiliation";
import { EEmploymentContractModificationType } from "@/features/anytime/constants/enums";
import { employmentContractSchema } from "@/features/anytime/schema/employmentContractSchema";
import { TEmploymentContractForm } from "@/features/anytime/types/employmentContractForm";
import { convertFormDataToReqDto } from "@/features/anytime/utils/employment-contract";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import { FC, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TAnytimeEmploymentContractCreatePageProps = {
  defaultValues: Partial<TEmploymentContractForm>;
};

export const AnytimeEmploymentContractCreatePage: FC<TAnytimeEmploymentContractCreatePageProps> = ({ defaultValues }) => {
  const navigate = useNavigate();
  const methods = useForm<TEmploymentContractForm>({
    defaultValues,
    resolver: yupResolver(employmentContractSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { getValues } = methods;
  const modificationType = useWatch({ control: methods.control, name: "modificationType" });

  const handleSave = async (data: TEmploymentContractForm, isDraft: boolean) => {
    try {
      const payload = convertFormDataToReqDto(data, isDraft);
      const rs = await createAnytimeEmploymentContractApi(payload);
      if (isDraft) {
        methods.unregister();
      }
      navigate(getDynamicRoute(AppRoutes.anytimeDocument, { id: rs.data.data.anyTimeDocumentId }));
      toast.success(rs.data.message ?? API_SUCCESS_MESSAGE);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onSubmit = async (data: TEmploymentContractForm) => {
    await handleSave(data, false);
  };

  const saveDraft = async () => {
    await handleSave(getValues() as TEmploymentContractForm, true);
  };

  const labelReasonForm = useMemo(() => {
    if (modificationType === EEmploymentContractModificationType.MODIFY_CONTRACT) return "契約の変更";
    if (modificationType === EEmploymentContractModificationType.TERMINATE_CONTRACT) return "契約の終了";
    if (modificationType === EEmploymentContractModificationType.RENEW_CONTRACT) return "新たな契約の締結";
    return null;
  }, [modificationType]);

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={employmentContractSchema}>
      <TopPage title="雇用契約の変更に係る届出" backUrl={AppRoutes.anytime} />
      <MenuNavigate
        contents={[
          { title: "対象の外国人", content: <EligibleForeignNationals /> },
          { title: "届出の事由", content: <ReasonForm /> },
          labelReasonForm ? { title: labelReasonForm, content: <Box /> } : [],
          { title: "特定技能所属機関", content: <SpecificSkillsAffiliation /> },
          { title: "届出担当者", content: <CompanySupporterForm /> },
        ].flat()}
        contentAffix={<ButtonGroup saveDraft={saveDraft} editable />}
      />
    </Form>
  );
};
